import { useState, useRef } from "react";
import { useAppContext } from "@/providers";
import { useLocation } from "react-router";
import { t } from "@/lib/i18n/messages";
import { performLogout } from "@/lib/auth/session-store";
import { useNotifications } from "@/hooks/use-notifications";
import { NotificationsPanel } from "@/components/layout/notifications-panel";
import { useDashboardShell } from "@/lib/layout/use-dashboard-shell";
import { cn } from "@/lib/utils";

const pageTitles: Record<string, { ar: string; en: string }> = {
  "/dashboard": { ar: "المحفظة", en: "Wallet" },
  "/dashboard/initiatives": { ar: "المبادرات", en: "Initiatives" },
  "/dashboard/contributions": { ar: "المساهمات", en: "Contributions" },
  "/dashboard/settings": { ar: "الإعدادات", en: "Settings" },
};

export function DashboardHeader() {
  const { toggleTheme, locale, theme, session, setSession } = useAppContext();
  const { pathname } = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const { notifications, unreadCount, loading, markRead, markAllRead } =
    useNotifications();
  const { isMobile, openDrawer } = useDashboardShell();

  const currentPath = pathname ?? "/dashboard";
  const basePathname =
    Object.keys(pageTitles).find(
      (key) =>
        currentPath === key ||
        (key !== "/dashboard" && currentPath.startsWith(key))
    ) ?? "/dashboard";
  const pageTitle =
    pageTitles[basePathname]?.[locale] ??
    pageTitles["/dashboard"]?.[locale] ??
    "";

  const isDark = theme === "dark";
  const userName = session.user?.name ?? (locale === "ar" ? "المستخدم" : "User");

  return (
    <header className="h-16 px-4 md:px-8 flex items-center justify-between bg-card/80 backdrop-blur-md sticky top-0 z-10 border-b border-border">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={openDrawer}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-base"
            aria-label="Open menu"
          >
            <span className="material-icons-outlined text-foreground-secondary">
              menu
            </span>
          </button>
        )}
        <h1 className="text-lg md:text-xl font-bold text-foreground">
          {pageTitle}
        </h1>
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        {/* Theme toggle */}
        <button
          className={cn(
            "w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-base",
            "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          <span className="material-icons-outlined text-xl">
            {isDark ? "light_mode" : "dark_mode"}
          </span>
        </button>

        {/* Notifications */}
        <button
          ref={bellRef}
          className={cn(
            "w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-base relative",
            "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
          onClick={() => setPanelOpen((prev) => !prev)}
          aria-label={t(locale, "notificationsTitle")}
          aria-expanded={panelOpen}
        >
          <span className="material-icons-outlined text-xl">notifications</span>
          {unreadCount > 0 && (
            <span className="absolute top-0.5 end-0.5 min-w-[16px] h-4 bg-error text-[10px] text-white rounded-pill flex items-center justify-center font-bold px-1">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-border mx-1 hidden md:block" />

        {/* User info */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground-secondary hidden md:inline">
            {userName}
          </span>
          <button
            onClick={async () => {
              const nextState = await performLogout();
              setSession(nextState);
            }}
            className={cn(
              "w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-base",
              "text-muted-foreground hover:text-error hover:bg-error/10"
            )}
            aria-label={t(locale, "logout")}
          >
            <span className="material-icons-outlined text-xl">logout</span>
          </button>
        </div>
      </div>
      {panelOpen && (
        <NotificationsPanel
          notifications={notifications}
          loading={loading}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onClose={() => setPanelOpen(false)}
          anchorRef={bellRef}
        />
      )}
    </header>
  );
}
